import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { marked } from 'marked';
import { useDocumentMeta } from '../lib/useDocumentMeta';

type Article = {
  slug: string;
  title: string;
  author: string;
  date: string;
  content: string;
};

marked.setOptions({ gfm: true, breaks: false });

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'missing' | 'error'>(
    'loading'
  );

  useDocumentMeta({
    title: article ? `${article.title} — Samia Systems` : 'Blog — Samia Systems',
    description: article ? article.title : 'Article',
  });

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    fetch(`/api/article?slug=${encodeURIComponent(slug)}`)
      .then(async (r) => {
        if (r.status === 404) {
          if (alive) setStatus('missing');
          return null;
        }
        if (!r.ok) {
          if (alive) setStatus('error');
          return null;
        }
        return r.json() as Promise<Article>;
      })
      .then((data) => {
        if (!alive || !data) return;
        setArticle(data);
        setStatus('ok');
      })
      .catch(() => alive && setStatus('error'));
    return () => {
      alive = false;
    };
  }, [slug]);

  const html = article ? (marked.parse(article.content) as string) : '';

  return (
    <section className="grain relative bg-black px-6 pb-32 pt-40">
      <div className="container-x max-w-3xl">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-label text-stone hover:text-gold"
        >
          <span aria-hidden>←</span> All articles
        </Link>

        {status === 'loading' && (
          <p className="mt-12 font-mono text-[0.7rem] uppercase tracking-label text-stone">
            Loading…
          </p>
        )}

        {status === 'missing' && (
          <div className="mt-12 rounded-4xl border border-white/10 bg-charcoal p-12">
            <h1 className="font-display text-3xl font-light text-white">
              Article not found.
            </h1>
            <p className="mt-4 font-sans text-body text-white/80">
              It may have been moved or unpublished.
            </p>
          </div>
        )}

        {status === 'error' && (
          <p className="mt-12 font-sans text-body text-white/80">
            Could not load this article. Try again in a moment.
          </p>
        )}

        {status === 'ok' && article && (
          <article className="mt-12">
            <div className="flex items-center gap-3 font-mono text-[0.65rem] uppercase tracking-label text-stone">
              <span>{article.date}</span>
              <span className="h-px w-6 bg-stone/40" />
              <span>{article.author}</span>
            </div>
            <h1 className="mt-6 font-display text-5xl font-light leading-tight text-white text-balance">
              {article.title}
            </h1>
            <div
              className="prose-samia mt-12"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </article>
        )}
      </div>
    </section>
  );
}
